import { useEffect, useState ,  useMemo,} from "react";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import Pagination from "../../components/Table/pagination";
import useFetch from "../../hooks/useFetch";

const API_URL = process.env.REACT_APP_API_URL;

const DNCStatusReport = () => {
  const { auth } = useAuth();

  const [dncLogs, setDncLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [campaignSearch, setCampaignSearch] = useState("");
  const [showCampaignMenu, setShowCampaignMenu] = useState(false);
  const [campaign, setCampaign] = useState("");

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
  });

  const [searchText, setSearchText] = useState("");
  const [campaignList, setCampaignList] = useState([]);
  const [dncFilter, setDncFilter] = useState("");

  const handleCampaignSelect = (option) => {
    setCampaign(option.id);
    setCampaignSearch(option.name);
    setShowCampaignMenu(false);
  };

  const filteredCampaigns = useMemo(() => {
    const searchValue = campaignSearch.trim().toLowerCase();
    if (!searchValue) {
      return campaignList;
    }
    return campaignList.filter((item) => item?.name?.toLowerCase().includes(searchValue));
  }, [campaignList, campaignSearch]);

  const handleCampaignInputChange = (event) => {
    const value = event.target.value;

    setCampaignSearch(value);
    setShowCampaignMenu(true);

    if (!value.trim()) {
      setCampaign("");
    }
  };
  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/contactcentre/all-campaigns/`,
        {
            params: {
            user_id: auth?.user_details?.user,
            user_type: auth?.user_details?.type,
            }
        }
      );

      setCampaignList(response.data?.results || response.data || []);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  const fetchDNCStatusReport = async (page = 1) => {
    try {
      setLoading(true);

      const params = {
        page,
      };

      if (searchText) {
        params.search = searchText;
      }

      if (campaign) {
        params.campaign_customer__campaign = campaign;
      }

      if (dncFilter !== "") {
        params.dnc = dncFilter;
      }

      const response = await axios.get(
        `${API_URL}/contactcentre/dnc-status-report/`,
        {
          params: {
            ...params,
            user_id: auth?.user_details?.id,
          },
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      setDncLogs(response.data?.results || []);

      setPagination({
        current_page: response.data?.current_page || 1,
        total_pages: response.data?.total_pages || 1,
        total_records: response.data?.count || 0,
      });
    } catch (error) {
      console.error("Error fetching DNC Status Report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.accessToken) {
      fetchCampaigns();
      fetchDNCStatusReport(1);
    }
  }, [auth?.accessToken]);

  const PaginationControl = (type, pageNumber = null) => {
    let newPage = pagination.current_page;

    switch (type) {
      case "page":
        newPage = pageNumber;
        break;
      case "prev":
        newPage = Math.max(1, pagination.current_page - 1);
        break;
      case "next":
        newPage = Math.min(
          pagination.total_pages,
          pagination.current_page + 1
        );
        break;
      case "first":
        newPage = 1;
        break;
      case "last":
        newPage = pagination.total_pages;
        break;
      default:
        return;
    }

    fetchDNCStatusReport(newPage);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <h1 className="font-bold text-lg mb-4">DNC Status Report</h1>
      {/* Filters */}
      <div className="mb-5">
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:flex 2xl:flex-wrap 2xl:items-end">
          {/* Global Search */}
          <div className="relative w-full 2xl:max-w-[250px]">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                />
              </svg>
            </div>

            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchDNCStatusReport(1);
                }
              }}
              placeholder="Search customer, user..."
              className="h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Campaign Filter */}
            <div className="relative w-full 2xl:max-w-[220px]">
                <input
                type="text"
                value={campaignSearch}
                onChange={handleCampaignInputChange}
                onFocus={() =>
                    setShowCampaignMenu(true)
                }
                onBlur={() =>
                    window.setTimeout(
                    () =>
                        setShowCampaignMenu(false),
                    150
                    )
                }
                placeholder="Select campaign"
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

                {showCampaignMenu && (
                <ul className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                    {filteredCampaigns.map(
                    (option) => (
                        <li
                        key={option.id}
                        onMouseDown={() =>
                            handleCampaignSelect(
                            option
                            )
                        }
                        className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
                        >
                        {option.name}
                        </li>
                    )
                    )}

                    {filteredCampaigns.length ===
                    0 && (
                    <li className="px-4 py-2 text-sm text-gray-500">
                        No campaigns found
                    </li>
                    )}
                </ul>
                )}
            </div>

          {/* Search Button */}
          <button
            type="button"
            disabled={loading}
            onClick={() => fetchDNCStatusReport(1)}
            className="h-10 w-[100px] rounded-md bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Loading..." : "Search"}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-500">
                Sl No
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-500">
                Customer
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-500">
                Customer Number
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-500">
                Campaign
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-500">
                User
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-500">
                DNC Status
              </th>
              <th className="px-6 py-4 text-left text-sm font-bold text-gray-500">
                Created At
              </th>
            </tr>
          </thead>

          <tbody>
            {dncLogs.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="py-8 text-center text-gray-500"
                >
                  {loading ? "Loading..." : "No records found"}
                </td>
              </tr>
            ) : (
              dncLogs.map((item, index) => (
                <tr
                  key={item.id}
                  className="border-b hover:bg-gray-50"
                >
                  <td className="px-6 py-4 text-sm">
                    {(pagination.current_page - 1) * 10 + index + 1}
                  </td>

                  <td className="px-6 py-4 text-sm">
                    {item.customer_name || "-"}
                  </td>

                  <td className="px-6 py-4 text-sm">
                    {item.customer_number || "-"}
                  </td>

                  <td className="px-6 py-4 text-sm">
                    {item.campaign || "-"}
                  </td>

                    <td className="px-6 py-4 text-sm">
                    {item.user_name || "-"}
                    {item.user_type === "sys_admin"
                        ? " (Admin)"
                        : item.user_type === "sys_user"
                        ? " (Agent)"
                        : ""}
                    </td>

                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                        item.dnc
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {item.dnc ? "Yes" : "No"}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-sm">
                    {item.created_at
                      ? new Date(item.created_at).toLocaleString()
                      : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          pagination_data={pagination}
          PaginationControl={PaginationControl}
        />
      </div>
    </div>
  );
};

export default DNCStatusReport;